import { useCallback, useMemo } from 'react';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { ProductOffering } from './ConciergeServiceSection.types';
import type { ConciergeServiceCategory } from '@apps/card/routes/Concierge/config';
import { useInvitationOfferingsData, useSaveTheDateOfferingsData } from '@apps/card/routes/Dashboard/components/ConciergeServiceSection/ConciergeServiceSection.utils';
import { seededShuffle } from '@apps/card/utils/seededRandom';

type UseProductOfferingsOptions = Readonly<{
  /** Order the offerings by this seed instead of at random, so a server render and its hydration agree (PRNT-5494). */
  seed?: string;
}>;

export const useProductOfferings = (
  options: UseProductOfferingsOptions = {},
): {
  productOfferings: Record<ConciergeServiceCategory, ReadonlyArray<ProductOffering>>;
  getConciergeCategoryFromDesignSetName: (designSetName: string) => ConciergeServiceCategory;
  getProductOfferingFromDesignSetName: (designSetName: string) => ProductOffering;
  getProductOfferingFromCategoryAndId: (category: ConciergeServiceCategory, id: string) => ProductOffering | undefined;
} => {
  const { seed } = options;
  const invitationOfferings = useInvitationOfferingsData();
  const saveTheDateOfferings = useSaveTheDateOfferingsData();

  const productOfferings = useMemo(() => {
    const shuffle = (offerings: ProductOffering[]): ReadonlyArray<ProductOffering> =>
      seed !== undefined ? seededShuffle(offerings, seed) : offerings.sort(() => Math.random() - 0.5);
    return {
      [StationeryTemplateCategoryEnum.invitation]: shuffle(Object.values(invitationOfferings) as ProductOffering[]),
      [StationeryTemplateCategoryEnum.saveTheDate]: shuffle(Object.values(saveTheDateOfferings) as ProductOffering[]),
    };
  }, [invitationOfferings, saveTheDateOfferings, seed]);

  const getConciergeCategoryFromDesignSetName = useCallback(
    (designSetName: string) => {
      return Object.keys(productOfferings).find(key =>
        productOfferings[key as ConciergeServiceCategory].some(offering => offering.identifier === designSetName),
      ) as ConciergeServiceCategory;
    },
    [productOfferings],
  );

  const getProductOfferingFromDesignSetName = useCallback(
    (designSetName: string) => {
      return Object.values(productOfferings)
        .flat()
        .find(offering => offering.identifier === designSetName) as ProductOffering;
    },
    [productOfferings],
  );

  const getProductOfferingFromCategoryAndId = useCallback(
    (category: ConciergeServiceCategory, id: string) => {
      return productOfferings[category].find(offering => offering.id === id) as ProductOffering | undefined;
    },
    [productOfferings],
  );

  return {
    productOfferings,
    getConciergeCategoryFromDesignSetName,
    getProductOfferingFromDesignSetName,
    getProductOfferingFromCategoryAndId,
  };
};
